import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Text from '@/components/Text';
import { InvoiceResource, MediaResource } from '@/types/resources';

import InvoicePdfModal from './InvoicePdfModal';

const Holder = styled.div`
  width: 195px;
  height: 36px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  color: ${({ theme }) => theme.colors.gray};
  border-radius: 4px;
  display: flex;
  padding: 6px 8px 6px 12px;
  align-items: center;
  justify-content: space-between;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FileName = styled(Text)`
  font-weight: 400;
  width: 87px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
  border: solid 1px transparent;
  transition: all ease-out 0.2s;
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const NameHolder = styled.div`
  display: flex;
  align-items: center;
  > svg {
    margin-right: 6px;
  }
`;

interface Props {
  file: MediaResource;
  invoiceId: number;
  invoice?: InvoiceResource;
  isOriginal?: boolean;
  isProforma?: boolean;
  downloadUrl?: string;
  raw?: boolean;
  onRemove?: () => void;
}

const InvoicesFileBlock: FC<Props> = ({
  file,
  invoiceId,
  invoice,
  isOriginal,
  isProforma,
  downloadUrl,
  raw,
  onRemove,
}) => {
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const downloadParams = new URLSearchParams({
    ...(isOriginal && { original: 'true' }),
    ...(isProforma && { proforma: 'true' }),
    ...(raw && !isOriginal && { raw: 'true' }),
  }).toString();

  const apiDownloadHref = `${process.env.REACT_APP_API_ENDPOINT}/invoices/${invoiceId}${downloadParams ? `?${downloadParams}` : ''}`;

  const downloadHref = downloadUrl ?? apiDownloadHref;

  const regexForFileFormat = /(?:\.([^.]+))?$/;
  const fileFormat = regexForFileFormat.exec(file.name)?.[1];

  return (
    <Holder>
      <NameHolder>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.2839 6.57091L6.33334 12.6203C6.11901 12.8346 5.99861 13.1253 5.99861 13.4284C5.99861 13.7315 6.11901 14.0222 6.33334 14.2365C6.54767 14.4508 6.83836 14.5713 7.14146 14.5713C7.44457 14.5713 7.73526 14.4508 7.94959 14.2365L15.043 7.04429C15.2553 6.83204 15.4236 6.58007 15.5385 6.30275C15.6534 6.02544 15.7125 5.72821 15.7125 5.42805C15.7125 5.12788 15.6534 4.83066 15.5385 4.55334C15.4236 4.27603 15.2553 4.02405 15.043 3.8118C14.8308 3.59956 14.5788 3.43119 14.3015 3.31632C14.0241 3.20146 13.7269 3.14233 13.4268 3.14233C13.1266 3.14233 12.8294 3.20146 12.5521 3.31632C12.2747 3.43119 12.0228 3.59956 11.8105 3.8118L4.7171 11.004C4.07411 11.647 3.71289 12.5191 3.71289 13.4284C3.71289 14.3377 4.07411 15.2098 4.7171 15.8528C5.36008 16.4957 6.23215 16.857 7.14146 16.857C8.05078 16.857 8.92285 16.4957 9.56583 15.8528L15.4268 9.99948"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <FileName $type="smallMedium">
          {isOriginal ? `Original ${file.name}` : file.name}
        </FileName>
      </NameHolder>
      <ActionsWrapper>
        <ActionButton as="a" href={downloadHref} download>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 6.78571L6 10.7857L10 6.78571"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 1.64282V10.7857"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ActionButton>
        {!!onRemove && (
          <ActionButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onRemove();
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 3L3 9M3 3L9 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </ActionButton>
        )}
        {fileFormat?.toLowerCase() === 'pdf' && (
          <ActionButton onClick={() => setShowInvoiceModal(true)}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.4375 9.375C7.61212 9.375 9.375 7.61212 9.375 5.4375C9.375 3.26288 7.61212 1.5 5.4375 1.5C3.26288 1.5 1.5 3.26288 1.5 5.4375C1.5 7.61212 3.26288 9.375 5.4375 9.375Z"
                stroke="currentCOlor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.22168 8.22192L10.4998 10.5001"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ActionButton>
        )}
      </ActionsWrapper>
      <AnimatePresence>
        {showInvoiceModal && (
          <InvoicePdfModal
            file={file.versions.original}
            fileName={file.name}
            downloadLink={apiDownloadHref}
            onClose={() => setShowInvoiceModal(false)}
            invoice={invoice}
          />
        )}
      </AnimatePresence>
    </Holder>
  );
};

export default InvoicesFileBlock;
